import React, { useCallback, useEffect, useState } from "react";
import {
  compareBoqSnapshots,
  fetchBoqSnapshots,
  restoreBoqSnapshot,
  branchBoqSnapshot,
  fetchSnapshotRowDiff,
  fetchRestorePreview,
  exportProjectBoq,
  pruneSnapshots,
} from "../api/productTablesApi";
import { confirmDialog } from "../utils/confirmDialog";
import BoqShareSection from "./BoqShareSection";

const fmt = (v) => {
  if (v == null || v === "") return "—";
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "0";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const delta = (v) => {
  if (v == null) return null;
  const n = parseFloat(v);
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
};

function DeltaCell({ value }) {
  const n = delta(value);
  if (n === null) return <td style={styles.td}>—</td>;
  const positive = n > 0;
  return (
    <td
      style={{
        ...styles.td,
        color: positive ? "#1a8a3a" : "#c0392b",
        fontWeight: 600,
      }}
    >
      {positive ? "▲ " : "▼ "}
      {fmt(Math.abs(n))}
    </td>
  );
}

function ValueCell({ a, b }) {
  const na = parseFloat(a) || 0;
  const nb = parseFloat(b) || 0;
  const diff = nb - na;
  const color = diff > 0 ? "#1a8a3a" : diff < 0 ? "#c0392b" : "#666";
  return (
    <td style={styles.td}>
      <span style={{ color: "#888", fontSize: 11 }}>{fmt(a)}</span>
      <span style={{ color: "#bbb", margin: "0 4px" }}>→</span>
      <span style={{ color, fontWeight: 600 }}>{fmt(b)}</span>
    </td>
  );
}

function SnapshotRow({ snap, isSelected, onSelect, onRestore, restoring }) {
  const d = new Date(snap.created_at);
  const dateStr = d.toLocaleString("ka-GE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <div
      onClick={() => onSelect(snap)}
      style={{
        ...styles.timelineRow,
        background: isSelected ? "#1a3a5a" : "#1e2a38",
        borderLeft: isSelected ? "3px solid #2196f3" : "3px solid transparent",
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: "#ccc", fontSize: 12 }}>{dateStr}</span>
        {snap.version_name && (
          <span style={{ background: "#2a4a6a", color: "#7bc8ff", fontSize: 11, padding: "1px 6px", borderRadius: 4 }}>
            {snap.version_name}
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
        <span style={{ color: "#aaa", fontSize: 11 }}>
          {snap.created_by_name || "—"}
        </span>
        <span style={{ color: "#e0e0e0", fontSize: 12, fontWeight: 600 }}>
          ₾ {fmt(snap.total_cost)}
        </span>
      </div>
      {snap.label && (
        <div style={{ color: "#7bc8ff", fontSize: 11, marginTop: 2, fontStyle: "italic" }}>
          {snap.label}
        </div>
      )}
      {onRestore && snap.has_payload && (
        <button
          onClick={(e) => { e.stopPropagation(); onRestore(snap); }}
          disabled={restoring}
          style={{
            marginTop: 6, padding: "3px 10px", fontSize: 11, borderRadius: 6, cursor: "pointer",
            background: "transparent", border: "1px solid #f59e0b", color: "#f59e0b",
          }}
        >
          {restoring ? "…" : "↩ აღდგენა"}
        </button>
      )}
    </div>
  );
}

export default function BoqHistoryPanel({ token, projectId, onClose, canRestore = false, onRestored, onBranched, onApproved }) {
  const [branchName, setBranchName] = useState("");
  const [branching, setBranching] = useState(false);
  const [notice, setNotice] = useState("");
  const [restoringId, setRestoringId] = useState(null);
  const [backingUp, setBackingUp] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedA, setSelectedA] = useState(null);
  const [selectedB, setSelectedB] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [mode, setMode] = useState("timeline"); // "timeline" | "compare"

  const load = useCallback(async () => {
    if (!token || !projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchBoqSnapshots(token, projectId, 100, 0);
      setSnapshots(data.items || []);
    } catch (e) {
      setError(e.message || "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [token, projectId]);

  useEffect(() => { load(); }, [load]);

  const handleCompare = useCallback(async () => {
    if (!selectedA || !selectedB) return;
    setComparing(true);
    setError(null);
    try {
      const data = await compareBoqSnapshots(token, projectId, selectedA.id, selectedB.id);
      setComparison(data);
      setMode("compare");
    } catch (e) {
      setError(e.message || "Comparison failed");
    } finally {
      setComparing(false);
    }
  }, [token, projectId, selectedA, selectedB]);

  const handleRestore = useCallback(async (snap) => {
    const when = new Date(snap.created_at).toLocaleString("ka-GE");
    let changes = "";
    try {
      const pv = await fetchRestorePreview(token, projectId, snap.id);
      const m = pv.summary || {};
      const parts = [
        m.chapters_added && `+${m.chapters_added} თავი`,
        m.chapters_removed && `−${m.chapters_removed} თავი`,
        m.rows_added && `+${m.rows_added} სტრიქონი`,
        m.rows_removed && `−${m.rows_removed} სტრიქონი`,
        m.price_changes && `${m.price_changes} ფასი შეიცვლება`,
      ].filter(Boolean);
      changes = parts.length ? `\n\nცვლილებები: ${parts.join(", ")}.` : "\n\nშინაარსი ახლანდელის იდენტურია.";
    } catch {
      /* preview is optional */
    }
    const ok = await confirmDialog(
      `BOQ დაბრუნდება ${when} შენახვის მდგომარეობაზე (${snap.created_by_name || "—"}).${changes}\n\n` +
      "ახლანდელი მდგომარეობა ავტომატურად შეინახება, ასე რომ აღდგენის გაუქმებაც შესაძლებელია. გაგრძელება?"
    );
    if (!ok) return;
    setRestoringId(snap.id);
    setError(null);
    try {
      await restoreBoqSnapshot(token, projectId, snap.id);
      await load();
      if (typeof onRestored === "function") onRestored(snap);
    } catch (e) {
      setError(e.message || "Restore failed");
    } finally {
      setRestoringId(null);
    }
  }, [token, projectId, load, onRestored]);

  const handleBranch = useCallback(async () => {
    if (!selectedA || !branchName.trim()) return;
    setBranching(true);
    setError(null);
    setNotice("");
    try {
      const version = await branchBoqSnapshot(token, projectId, selectedA.id, branchName.trim());
      setNotice(`✓ ვერსია „${version.version_name || branchName.trim()}“ შეიქმნა. აირჩიე ვერსიების სიაში და დააჭირე Load.`);
      setBranchName("");
      if (typeof onBranched === "function") await onBranched(version);
    } catch (e) {
      setError(e.message || "Branch failed");
    } finally {
      setBranching(false);
    }
  }, [token, projectId, selectedA, branchName, onBranched]);

  const handlePrune = useCallback(async () => {
    setError(null);
    setNotice("");
    try {
      const r = await pruneSnapshots(token, projectId);
      setNotice(r.deleted ? `✓ წაიშალა ${r.deleted} ძველი შენახვა (რჩება ბოლო ${r.keep_per_version} თითო ვერსიაზე).` : `✓ ძველი შენახვები არ არის (რჩება ბოლო ${r.keep_per_version} თითო ვერსიაზე).`);
      await load();
    } catch (e) {
      setError(e.message || "Cleanup failed");
    }
  }, [token, projectId, load]);

  const handleBackup = useCallback(async () => {
    setBackingUp(true);
    setError(null);
    try {
      const data = await exportProjectBoq(token, projectId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `boq-backup-project-${projectId}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || "Backup failed");
    } finally {
      setBackingUp(false);
    }
  }, [token, projectId]);

  const handleSelectTimeline = (snap) => {
    if (!selectedA) {
      setSelectedA(snap);
    } else if (!selectedB && snap.id !== selectedA.id) {
      setSelectedB(snap);
    } else {
      setSelectedA(snap);
      setSelectedB(null);
      setComparison(null);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        {/* Header */}
        <div style={styles.header}>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>
            ისტორია / Save History
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={handleBackup}
              disabled={backingUp}
              title="Download the whole project BOQ as a JSON backup"
              style={{ ...styles.closeBtn, fontSize: 11, padding: "3px 8px", border: "1px solid #3a5a7a", borderRadius: 6 }}
            >
              {backingUp ? "…" : "⬇ Backup"}
            </button>
            {canRestore && (
              <button
                onClick={handlePrune}
                title="ძველი შენახვების წაშლა — რჩება ბოლო 3 თითო ვერსიაზე"
                style={{ ...styles.closeBtn, fontSize: 11, padding: "3px 8px", border: "1px solid #3a5a7a", borderRadius: 6 }}
              >
                🧹
              </button>
            )}
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
        </div>

        {/* Tab switcher */}
        <div style={styles.tabs}>
          <button
            onClick={() => setMode("timeline")}
            style={{ ...styles.tab, ...(mode === "timeline" ? styles.tabActive : {}) }}
          >
            Timeline
          </button>
          <button
            onClick={() => setMode("compare")}
            style={{ ...styles.tab, ...(mode === "compare" ? styles.tabActive : {}) }}
            disabled={!comparison}
          >
            შედარება / Compare
          </button>
        </div>

        {error && (
          <div style={styles.errorBox}>{error}</div>
        )}

        {mode === "timeline" && (
          <div style={styles.body}>
            {/* Selection hints */}
            <div style={styles.selHints}>
              <div style={{ ...styles.selBadge, background: selectedA ? "#1a4a8a" : "#2a2a2a" }}>
                A: {selectedA ? new Date(selectedA.created_at).toLocaleDateString() : "select…"}
              </div>
              <div style={{ ...styles.selBadge, background: selectedB ? "#1a4a8a" : "#2a2a2a" }}>
                B: {selectedB ? new Date(selectedB.created_at).toLocaleDateString() : "select…"}
              </div>
              <button
                onClick={handleCompare}
                disabled={!selectedA || !selectedB || comparing}
                style={{
                  ...styles.compareBtn,
                  opacity: (!selectedA || !selectedB) ? 0.4 : 1,
                }}
              >
                {comparing ? "…" : "Compare →"}
              </button>
            </div>

            <div style={{ padding: "6px 10px", fontSize: 11, color: "#888" }}>
              ინახება ბოლო 3 შენახვა თითო ვერსიაზე (ძველები ავტომატურად იშლება; დამტკიცებული ან ბმულით გაზიარებული შენახვა რჩება).
            </div>

            {canRestore && (
              <div style={{ padding: "8px 10px", borderBottom: "1px solid #2a3a4a" }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 6 }}>
                  ახალი ვერსია არჩეული შენახვიდან (სცენარი — მიმდინარე ვერსია არ შეიცვლება)
                  {selectedA ? ` · A: ${new Date(selectedA.created_at).toLocaleDateString()}` : " · ჯერ აირჩიე შენახვა"}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    type="text" value={branchName} onChange={(e) => setBranchName(e.target.value)}
                    placeholder="ვერსიის სახელი, მაგ. „პრემიუმ მოპირკეთება“"
                    maxLength={60}
                    style={{ flex: 1, padding: "5px 8px", borderRadius: 6, fontSize: 12, boxSizing: "border-box" }}
                  />
                  <button
                    onClick={handleBranch}
                    disabled={!selectedA || !selectedA.has_payload || !branchName.trim() || branching}
                    title={selectedA && !selectedA.has_payload ? "ამ შენახვას შინაარსი არ აქვს" : ""}
                    style={{ ...styles.compareBtn, opacity: (!selectedA || !selectedA.has_payload || !branchName.trim()) ? 0.4 : 1 }}
                  >
                    {branching ? "…" : "⑂ ახალი ვერსია"}
                  </button>
                </div>
                {notice && <div style={{ marginTop: 6, fontSize: 11, color: "#34d399" }}>{notice}</div>}
              </div>
            )}

            {canRestore && (
              <BoqShareSection token={token} projectId={projectId} snap={selectedA} onApproved={onApproved} />
            )}

            {loading ? (
              <div style={styles.center}>Loading…</div>
            ) : snapshots.length === 0 ? (
              <div style={styles.center}>No saves yet</div>
            ) : (
              <div style={styles.scrollList}>
                {snapshots.map((s) => (
                  <SnapshotRow
                    key={s.id}
                    snap={s}
                    isSelected={s.id === selectedA?.id || s.id === selectedB?.id}
                    onSelect={handleSelectTimeline}
                    onRestore={canRestore ? handleRestore : null}
                    restoring={restoringId === s.id}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {mode === "compare" && comparison && (
          <CompareView comparison={comparison} token={token} projectId={projectId} />
        )}
      </div>
    </div>
  );
}

const KIND_STYLE = {
  row_added: { icon: "＋", color: "#34d399", label: "დაემატა" },
  row_removed: { icon: "−", color: "#fb7185", label: "წაიშალა" },
  row_changed: { icon: "✎", color: "#f59e0b", label: "შეიცვალა" },
  chapter_added: { icon: "＋", color: "#34d399", label: "ახალი თავი" },
  chapter_removed: { icon: "−", color: "#fb7185", label: "თავი წაიშალა" },
  chapter_renamed: { icon: "✎", color: "#f59e0b", label: "გადარქმეულია" },
};
const FIELD_LABEL = {
  unit_rate: "ფასი", qty_per_work_unit: "რაოდ.", base_unit: "ერთ.", material_name_geo: "სახელი", resource_type: "ტიპი",
};

function RowDiffSection({ token, projectId, aId, bId }) {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: "" });
    fetchSnapshotRowDiff(token, projectId, aId, bId)
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: "" }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, data: null, error: e.message || "" }); });
    return () => { cancelled = true; };
  }, [token, projectId, aId, bId]);

  if (state.loading) return <div style={{ padding: 10, color: "#888", fontSize: 11 }}>სტრიქონების შედარება…</div>;
  if (state.error) {
    return (
      <div style={{ padding: 10, color: "#888", fontSize: 11 }}>
        სტრიქონების დონეზე შედარება მიუწვდომელია (ერთ-ერთ შენახვას შინაარსი არ აქვს).
      </div>
    );
  }
  const { summary: m, items, truncated } = state.data;
  const chips = [
    ["+ სტრიქონი", m.rows_added, "#34d399"], ["− სტრიქონი", m.rows_removed, "#fb7185"],
    ["✎ შეცვლილი", m.rows_changed, "#f59e0b"], ["ფასის ცვლილება", m.price_changes, "#7bc8ff"],
  ].filter(([, n]) => n);
  return (
    <div style={{ padding: "0 8px 12px" }}>
      <div style={styles.tableHeader}>სტრიქონების ცვლილებები</div>
      {items.length === 0 ? (
        <div style={{ padding: 8, color: "#34d399", fontSize: 12 }}>✓ სტრიქონები იდენტურია</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {chips.map(([label, n, color]) => (
              <span key={label} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, border: `1px solid ${color}`, color }}>
                {label}: {n}
              </span>
            ))}
          </div>
          {items.map((it, i) => {
            const st = KIND_STYLE[it.kind] || KIND_STYLE.row_changed;
            return (
              <div key={i} style={{ display: "flex", gap: 8, padding: "5px 6px", borderBottom: "1px solid #2a3a4a", fontSize: 11 }}>
                <span style={{ color: st.color, fontWeight: 700, width: 14, flexShrink: 0 }}>{st.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#ddd", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <span style={{ color: "#888" }}>{it.row || it.chapter} </span>{it.name}
                  </div>
                  <div style={{ color: "#888" }}>
                    {it.chapter_name ? `${it.chapter}. ${it.chapter_name} · ` : ""}{st.label}
                    {it.old_name ? ` (${it.old_name} → ${it.name})` : ""}
                  </div>
                  {it.changes && (
                    <div style={{ color: st.color }}>
                      {Object.entries(it.changes).map(([f, [o, n]]) => (
                        <span key={f} style={{ marginRight: 10 }}>
                          {FIELD_LABEL[f] || f}: {String(o ?? "—")} → <strong>{String(n ?? "—")}</strong>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {truncated && <div style={{ padding: 6, color: "#888", fontSize: 11 }}>ნაჩვენებია პირველი {items.length} ცვლილება</div>}
        </>
      )}
    </div>
  );
}

function CompareView({ comparison, token, projectId }) {
  const { snapshot_a: sa, snapshot_b: sb, chapter_diffs } = comparison;

  const fmtDate = (s) => new Date(s.created_at).toLocaleString("ka-GE", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });

  return (
    <div style={{ overflowY: "auto", flex: 1 }}>
      {/* Totals summary */}
      <div style={styles.compareSummary}>
        <div style={styles.summaryLabel}>Snapshot A</div>
        <div style={styles.summaryDate}>{fmtDate(sa)}</div>
        <div style={styles.summaryTotal}>₾ {fmt(sa.total_cost)}</div>
      </div>
      <div style={{ textAlign: "center", color: "#7bc8ff", fontSize: 18, margin: "4px 0" }}>↕</div>
      <div style={styles.compareSummary}>
        <div style={styles.summaryLabel}>Snapshot B</div>
        <div style={styles.summaryDate}>{fmtDate(sb)}</div>
        <div style={styles.summaryTotal}>₾ {fmt(sb.total_cost)}</div>
      </div>

      {/* Aggregate deltas */}
      <div style={styles.deltaRow}>
        <DeltaLabel label="მასალა" a={sa.total_material} b={sb.total_material} />
        <DeltaLabel label="სამუშაო" a={sa.total_labour} b={sb.total_labour} />
        <DeltaLabel label="მექანიზმი" a={sa.total_equipment} b={sb.total_equipment} />
        <DeltaLabel label="სულ" a={sa.total_cost} b={sb.total_cost} bold />
      </div>

      {/* Chapter breakdown */}
      {chapter_diffs && chapter_diffs.length > 0 && (
        <div style={{ padding: "0 8px 8px" }}>
          <div style={styles.tableHeader}>Chapters</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>N</th>
                <th style={styles.th}>სახელი</th>
                <th style={styles.th}>მასალა</th>
                <th style={styles.th}>სამუშაო</th>
                <th style={styles.th}>მექანიზმი</th>
                <th style={styles.th}>სულ</th>
                <th style={styles.th}>Δ</th>
              </tr>
            </thead>
            <tbody>
              {chapter_diffs.map((ch) => (
                <tr key={ch.display_id} style={{ borderBottom: "1px solid #2a3a4a" }}>
                  <td style={{ ...styles.td, color: "#888", width: 28 }}>{ch.display_id}</td>
                  <td style={{ ...styles.td, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={ch.name}>
                    {ch.name}
                  </td>
                  <ValueCell a={ch.material_a} b={ch.material_b} />
                  <ValueCell a={ch.labour_a} b={ch.labour_b} />
                  <ValueCell a={ch.equipment_a} b={ch.equipment_b} />
                  <ValueCell a={ch.total_a} b={ch.total_b} />
                  <DeltaCell value={ch.delta_total} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RowDiffSection token={token} projectId={projectId} aId={sa.id} bId={sb.id} />
    </div>
  );
}

function DeltaLabel({ label, a, b, bold }) {
  const diff = (parseFloat(b) || 0) - (parseFloat(a) || 0);
  const color = diff > 0 ? "#1a8a3a" : diff < 0 ? "#c0392b" : "#aaa";
  return (
    <div style={{ flex: 1, textAlign: "center", padding: "4px 0" }}>
      <div style={{ color: "#888", fontSize: 10 }}>{label}</div>
      <div style={{ color, fontWeight: bold ? 700 : 500, fontSize: 12 }}>
        {diff === 0 ? "—" : (diff > 0 ? "▲ " : "▼ ") + fmt(Math.abs(diff))}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    zIndex: 9999,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingTop: 6,
  },
  panel: {
    background: "#16202c",
    borderRadius: 8,
    width: "99vw",
    maxHeight: "97vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
    overflow: "hidden",
  },
  header: {
    background: "#0e1825",
    padding: "10px 14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #2a3a4a",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: "#aaa",
    fontSize: 16,
    cursor: "pointer",
    padding: "2px 6px",
  },
  tabs: {
    display: "flex",
    background: "#111a24",
    borderBottom: "1px solid #2a3a4a",
  },
  tab: {
    flex: 1,
    padding: "8px 4px",
    background: "transparent",
    border: "none",
    color: "#888",
    fontSize: 12,
    cursor: "pointer",
  },
  tabActive: {
    color: "#7bc8ff",
    borderBottom: "2px solid #7bc8ff",
    fontWeight: 600,
  },
  errorBox: {
    margin: 8,
    padding: "8px 12px",
    background: "#3a1a1a",
    color: "#ff8888",
    borderRadius: 4,
    fontSize: 12,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
  },
  selHints: {
    display: "flex",
    gap: 6,
    padding: "8px 10px",
    borderBottom: "1px solid #2a3a4a",
    alignItems: "center",
  },
  selBadge: {
    flex: 1,
    padding: "4px 8px",
    borderRadius: 4,
    color: "#ccc",
    fontSize: 11,
    textAlign: "center",
  },
  compareBtn: {
    padding: "5px 12px",
    background: "#1a5a8a",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    cursor: "pointer",
  },
  scrollList: {
    overflowY: "auto",
    flex: 1,
    padding: "6px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  timelineRow: {
    padding: "8px 10px",
    borderRadius: 4,
    border: "1px solid #2a3a4a",
  },
  center: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    color: "#666",
    fontSize: 13,
  },
  compareSummary: {
    background: "#1a2a3a",
    margin: "8px 8px 0",
    padding: "8px 12px",
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  summaryLabel: {
    background: "#2a4a6a",
    color: "#7bc8ff",
    fontSize: 10,
    padding: "2px 6px",
    borderRadius: 3,
  },
  summaryDate: { color: "#aaa", fontSize: 11, flex: 1 },
  summaryTotal: { color: "#e0e0e0", fontWeight: 700, fontSize: 13 },
  deltaRow: {
    display: "flex",
    margin: "6px 8px",
    background: "#111a24",
    borderRadius: 4,
    padding: "6px 0",
  },
  tableHeader: {
    color: "#7bc8ff",
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 4,
    marginTop: 8,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    padding: "5px 7px",
    background: "#1a2a3a",
    color: "#7bc8ff",
    textAlign: "right",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "4px 7px",
    color: "#d0d0d0",
    textAlign: "right",
    verticalAlign: "middle",
  },
};
